// Thin API client for the PharmacoWork backend.
// - attaches the Bearer access token
// - on 401, transparently tries the refresh token once, then retries
// - on refresh failure, clears the session and bounces to /connexion
import { authTokens } from "./auth-tokens";

const BASE = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:4000/api";

/**
 * Racine de l'API, exposée pour les ressources que le navigateur va chercher
 * lui-même (`<img>`, `<audio>`, `<iframe>`) : celles-là ne passent pas par
 * `request()` et ne peuvent pas porter d'en-tête Authorization — elles
 * s'authentifient par la signature présente dans l'URL.
 */
export const API_BASE = BASE;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
    /**
     * Secondes à attendre avant de réessayer, quand la réponse le dit (429).
     * L'écran s'en sert pour désactiver son bouton jusqu'à l'échéance au lieu
     * de laisser rejouer une action dont on sait qu'elle échouera.
     */
    public retryAfter?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Délai d'attente d'un 429, en secondes.
 *
 * Deux sources, dans cet ordre : le corps (`retryAfter`, posé par le verrou par
 * adresse, qui connaît son échelle progressive) puis l'en-tête `Retry-After`
 * standard (posé par le throttler par IP). Aucune des deux : `undefined`, et
 * l'appelant retombe sur son propre délai par défaut plutôt que d'inventer une
 * échéance fausse.
 */
function retryAfterSeconds(res: Response, body: unknown): number | undefined {
  if (body && typeof body === "object" && "retryAfter" in body) {
    const value = (body as { retryAfter: unknown }).retryAfter;
    if (typeof value === "number" && Number.isFinite(value) && value > 0) {
      return Math.ceil(value);
    }
  }
  const header = Number(res.headers.get("Retry-After"));
  return Number.isFinite(header) && header > 0 ? Math.ceil(header) : undefined;
}

/**
 * `status` des erreurs survenues AVANT toute réponse du serveur : coupure
 * réseau, DNS, TLS, requête interrompue par une navigation. Aucun code HTTP
 * n'existe dans ce cas, d'où 0 — les appelants qui testent un statut précis
 * (401…) ne peuvent pas le confondre avec une réponse réelle.
 */
export const NETWORK_ERROR_STATUS = 0;

// Ces deux messages sont affichés TELS QUELS à l'utilisateur : les écrans font
// `e instanceof Error ? e.message : …`, ce qui est vrai pour n'importe quelle
// Error. Sans conversion ici, `fetch` laissait remonter son « Failed to fetch »
// brut — de l'anglais technique dans une application française, et sans dire ce
// qui compte vraiment : la requête n'est pas partie, rien n'a été enregistré.
const MESSAGE_RESEAU =
  "Connexion au serveur impossible. Vérifiez votre connexion, rien n'a été enregistré.";
const MESSAGE_REPONSE_ILLISIBLE =
  "Réponse inattendue du serveur. Réessayez dans un instant.";

/**
 * 429 — même raisonnement que ci-dessus, pour le cas qui trompe le plus.
 *
 * Les verrous anti-pilonnage de l'API répondent, par défaut, « ThrottlerException:
 * Too Many Requests » : de l'anglais technique, qui ne dit ni ce qui s'est passé
 * ni quoi faire. Affiché tel quel derrière un message d'écran approximatif, il a
 * déjà envoyé chercher une panne de serveur là où il suffisait d'attendre.
 *
 * Les verrous qui ont quelque chose de plus précis à dire posent leur PROPRE
 * message, en français (codes TOTP erronés, cf. `totp.service.ts`) : celui-là
 * est plus informatif que le nôtre, on le laisse passer intact.
 */
const MESSAGE_TROP_DE_TENTATIVES =
  "Trop de tentatives. Patientez quelques minutes avant de réessayer — le délai s'allonge à chaque essai.";

/** Formulations génériques des verrous, à remplacer ; toute autre est gardée. */
const VERROU_GENERIQUE = /^(ThrottlerException|Too Many Requests)/i;

function errorMessage(body: unknown, fallback: string): string {
  if (body && typeof body === "object" && "message" in body) {
    const m = (body as { message: unknown }).message;
    if (Array.isArray(m)) return m.join(", ");
    if (typeof m === "string") return m;
  }
  return fallback;
}

async function raw(path: string, init: RequestInit, withAuth: boolean): Promise<Response> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (withAuth) {
    const token = authTokens.access;
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }
  try {
    // `credentials: "include"` — indispensable depuis E13 : le refresh token est
    // un cookie `HttpOnly` posé par l'API, sur une origine différente de celle
    // du front. Sans cette option, le navigateur ne l'envoie tout simplement
    // pas, et chaque rechargement de page déconnecterait l'utilisateur.
    return await fetch(`${BASE}${path}`, {
      ...init,
      headers,
      credentials: "include",
    });
  } catch {
    // `fetch` ne rejette que sur une panne de TRANSPORT — un 4xx/5xx passe par
    // la voie normale. On la convertit en ApiError pour que tout le reste du
    // code n'ait affaire qu'à un seul type d'erreur, déjà en français.
    throw new ApiError(NETWORK_ERROR_STATUS, MESSAGE_RESEAU);
  }
}

/**
 * Échange le cookie de refresh contre un nouveau jeton d'accès.
 *
 * Aucun corps de requête depuis E13 : le refresh token est un cookie `HttpOnly`
 * que ce code ne peut pas lire — et n'a pas à lire. Le navigateur le joint tout
 * seul grâce à `credentials: "include"`.
 *
 * Single-flight : plusieurs 401 simultanés (une page qui charge cinq listes)
 * ne doivent déclencher qu'UNE rotation. Deux rotations concurrentes
 * consommeraient le même jeton et, sans la fenêtre de grâce côté API, seraient
 * prises pour un rejeu.
 */
let refreshing: Promise<boolean> | null = null;

/**
 * Refus pour abonnement suspendu — le code posé par l'API
 * (`officine-suspendue.ts`), distinct d'un 403 ordinaire.
 *
 * Un 403 ordinaire est un droit manquant sur UN écran : il s'affiche là. Celui-ci
 * vaut pour toute l'application : laissé tel quel, chaque écran afficherait sa
 * propre erreur, sans que personne ne dise pourquoi plus rien ne marche. On vide
 * donc la session et on renvoie vers la connexion, qui explique.
 */
const CODE_OFFICINE_SUSPENDUE = "OFFICINE_SUSPENDUE";

function estOfficineSuspendue(body: unknown): boolean {
  return (
    !!body &&
    typeof body === "object" &&
    "code" in body &&
    (body as { code: unknown }).code === CODE_OFFICINE_SUSPENDUE
  );
}

function quitterOfficineSuspendue(): void {
  authTokens.clear();
  if (typeof window !== "undefined" && !window.location.pathname.startsWith("/connexion")) {
    window.location.href = "/connexion?suspendue=1";
  }
}

async function rotate(): Promise<boolean> {
  const res = await raw("/auth/refresh", { method: "POST" }, false);
  if (!res.ok) {
    authTokens.clear();
    if (res.status === 403) {
      const body: unknown = await res.json().catch(() => undefined);
      if (estOfficineSuspendue(body)) quitterOfficineSuspendue();
    }
    return false;
  }
  const data = (await res.json()) as { accessToken: string };
  authTokens.set(data.accessToken);
  return true;
}

/**
 * Verrou INTER-ONGLETS.
 *
 * Le single-flight ci-dessous ne couvre qu'un seul contexte JS. Or tous les
 * onglets de l'application partagent le MÊME cookie de refresh : deux onglets
 * rechargés en même temps enverraient le même jeton, et le second se ferait
 * traiter comme un rejeu — ce que l'API considère à juste titre comme un vol,
 * en révoquant toute la famille. Un simple rechargement de deux onglets
 * épinglés déconnectait alors tous les appareils du membre.
 *
 * `navigator.locks` sérialise les rotations : le second onglet attend, puis
 * repart du cookie déjà renouvelé par le premier. C'est ce qui permet de garder
 * la détection de vol STRICTE côté serveur, sans fenêtre de grâce — donc sans
 * intervalle pendant lequel un jeton volé serait rejouable impunément.
 *
 * ── Le repli n'en était pas un ────────────────────────────────────────────────
 *
 * Sans `navigator.locks`, ce code repartait « simplement sur le comportement
 * d'avant » : aucune sérialisation. Or le comportement d'avant est exactement
 * celui que le verrou existe pour empêcher — deux onglets qui rotent ensemble,
 * un rejeu détecté, et le membre déconnecté de TOUS ses appareils. Sur les
 * navigateurs concernés, la fonctionnalité était donc absente précisément là où
 * elle comptait.
 *
 * Qui est concerné : Web Locks est arrivé avec Safari 15.4 (mars 2022). Sur iOS,
 * où tous les navigateurs sont WebKit, un iPhone resté en 15.0-15.3 n'a donc
 * aucun verrou — et c'est un parc résiduel mais réel, avec un symptôme
 * spectaculaire et incompréhensible pour qui le subit.
 *
 * D'où le bail en `localStorage` ci-dessous : partagé entre onglets de la même
 * origine, synchrone, et disponible bien avant Web Locks.
 */
const LOCK_KEY = "pw-refresh-lock";
/** Durée du bail : au-delà, on considère que le porteur a disparu. */
const LOCK_TTL_MS = 10_000;
const LOCK_POLL_MS = 50;
/** Au-delà, on renonce à attendre et on tente la rotation quand même. */
const LOCK_MAX_WAIT_MS = 15_000;

type Bail = { owner: string; expiresAt: number };

const pause = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Identifiant d'onglet — un simple départageur, JAMAIS un secret.
 *
 * `crypto.randomUUID` n'est pas utilisable ici : il est arrivé dans Safari 15.4,
 * c'est-à-dire exactement en même temps que Web Locks. Sur les navigateurs qui
 * empruntent ce repli, il n'existe donc pas non plus.
 */
function idOnglet(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function lireBail(): Bail | null {
  try {
    const brut = localStorage.getItem(LOCK_KEY);
    if (!brut) return null;
    const bail = JSON.parse(brut) as Bail;
    return typeof bail?.owner === "string" &&
      typeof bail?.expiresAt === "number"
      ? bail
      : null;
  } catch {
    // Valeur corrompue, ou stockage qui lève (navigation privée sur les vieux
    // Safari) : on se comporte comme si le verrou était libre.
    return null;
  }
}

/**
 * Prend le bail, ou renonce au bout de LOCK_MAX_WAIT_MS.
 *
 * Renoncer et tenter quand même plutôt que d'échouer : un bail bloqué ne doit
 * JAMAIS rendre la connexion impossible. Au pire on retombe sur le risque qui
 * existait avant ce correctif, au mieux — le cas courant — on l'évite.
 */
async function prendreBail(owner: string): Promise<boolean> {
  const echeance = Date.now() + LOCK_MAX_WAIT_MS;
  for (;;) {
    const tenu = lireBail();
    if (!tenu || tenu.expiresAt <= Date.now()) {
      localStorage.setItem(
        LOCK_KEY,
        JSON.stringify({ owner, expiresAt: Date.now() + LOCK_TTL_MS }),
      );
      // Deux onglets peuvent avoir écrit dans le même souffle. `localStorage`
      // n'offre pas de compare-and-swap, mais le dernier écrivain gagne : une
      // relecture après un battement suffit à départager, et le perdant
      // repassera par la boucle.
      await pause(LOCK_POLL_MS);
      if (lireBail()?.owner === owner) return true;
    }
    if (Date.now() >= echeance) return false;
    await pause(LOCK_POLL_MS);
  }
}

function rendreBail(owner: string): void {
  try {
    // Seulement si on le tient encore : un bail expiré puis repris par un autre
    // onglet ne doit pas être effacé par le porteur précédent.
    if (lireBail()?.owner === owner) localStorage.removeItem(LOCK_KEY);
  } catch {
    // Rien à faire : le bail expirera de lui-même.
  }
}

async function withStorageLock(
  task: () => Promise<boolean>,
): Promise<boolean> {
  if (typeof localStorage === "undefined") return task();
  const owner = idOnglet();
  let tenu = false;
  try {
    tenu = await prendreBail(owner);
  } catch {
    // Stockage indisponible : on tente la rotation sans verrou, comme avant.
  }
  try {
    return await task();
  } finally {
    if (tenu) rendreBail(owner);
  }
}

async function withCrossTabLock(task: () => Promise<boolean>): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.locks) {
    return navigator.locks.request("pw-refresh", task);
  }
  return withStorageLock(task);
}

export async function tryRefresh(): Promise<boolean> {
  if (!refreshing) {
    refreshing = withCrossTabLock(rotate).finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

type RequestOptions = { auth?: boolean };

async function request<T>(
  path: string,
  init: RequestInit = {},
  opts: RequestOptions = {},
): Promise<T> {
  const auth = opts.auth ?? true;
  let res = await raw(path, init, auth);

  if (res.status === 401 && auth) {
    const ok = await tryRefresh();
    if (ok) {
      res = await raw(path, init, true);
    } else {
      authTokens.clear();
      if (
        typeof window !== "undefined" &&
        !window.location.pathname.startsWith("/connexion")
      ) {
        window.location.href = "/connexion";
      }
      throw new ApiError(401, "Session expirée.");
    }
  }

  if (res.status === 204) return undefined as T;

  // Le corps peut ne pas être du JSON : page d'erreur HTML insérée par un
  // portail Wi-Fi ou un proxy d'opérateur, réponse tronquée par une coupure en
  // cours de lecture. `JSON.parse` laissait alors remonter son « Unexpected
  // token < » jusqu'à l'écran.
  let body: unknown;
  try {
    const text = await res.text();
    body = text ? JSON.parse(text) : undefined;
  } catch {
    throw new ApiError(
      res.ok ? NETWORK_ERROR_STATUS : res.status,
      res.ok ? MESSAGE_REPONSE_ILLISIBLE : res.statusText || MESSAGE_REPONSE_ILLISIBLE,
    );
  }

  if (!res.ok) {
    const message = errorMessage(body, res.statusText);
    if (res.status === 403 && estOfficineSuspendue(body)) {
      quitterOfficineSuspendue();
      throw new ApiError(403, message, body);
    }
    throw new ApiError(
      res.status,
      res.status === 429 && VERROU_GENERIQUE.test(message)
        ? MESSAGE_TROP_DE_TENTATIVES
        : message,
      body,
      res.status === 429 ? retryAfterSeconds(res, body) : undefined,
    );
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: "GET" }),
  post: <T>(path: string, data?: unknown, opts?: RequestOptions) =>
    request<T>(
      path,
      { method: "POST", body: data !== undefined ? JSON.stringify(data) : undefined },
      opts,
    ),
  patch: <T>(path: string, data?: unknown) =>
    request<T>(path, {
      method: "PATCH",
      body: data !== undefined ? JSON.stringify(data) : undefined,
    }),
  delete: <T>(path: string, data?: unknown) =>
    request<T>(path, {
      method: "DELETE",
      body: data !== undefined ? JSON.stringify(data) : undefined,
    }),
};
